import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Button, Space, Typography, message, Tag, Divider, Alert, Modal } from 'antd';
import { ApiOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, SaveOutlined, BugOutlined, ArrowLeftOutlined, LogoutOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useApiConfig } from '../hooks/useApiConfig';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { AuthContext } from '../context/AuthContext';
import DebugLogger from '../components/DebugLogger';
import ApiPresets from '../components/ApiPresets';
import TeamManagement from '../components/Team/TeamManagement';
import TokenManagement from '../components/Token/TokenManagement';

const { Title, Text, Paragraph } = Typography;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const { apiUrl, isLoading, saveApiUrl, resetToDefault, testConnection, defaultUrl } = useApiConfig();
  const [newUrl, setNewUrl] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [appInfo, setAppInfo] = useState(null);

  useEffect(() => {
    setNewUrl(apiUrl);
  }, [apiUrl]);

  // Load app version info on native platforms
  useEffect(() => {
    const loadAppInfo = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const info = await App.getInfo();
          setAppInfo(info);
          console.log('App Info:', info);
        } catch (error) {
          console.error('Failed to get app info:', error);
        }
      }
    };
    loadAppInfo();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveApiUrl(newUrl);
      if (result.success) {
        message.success('API URL saved successfully!');
      } else {
        message.error(result.error || 'Failed to save API URL');
      }
    } catch (error) {
      message.error('Error saving API URL');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      const result = await testConnection(newUrl);
      setConnectionStatus(result);

      if (result.success) {
        message.success('Connection successful!');
      } else {
        message.error(`Connection failed: ${result.message}`);
      }
    } catch (error) {
      message.error('Error testing connection');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    await resetToDefault();
    setNewUrl(defaultUrl);
    setConnectionStatus(null);
    message.info('Reset to default API URL');
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'Abmelden',
      icon: <ExclamationCircleOutlined />,
      content: 'Möchten Sie sich wirklich abmelden?',
      okText: 'Ja, abmelden',
      cancelText: 'Abbrechen',
      onOk: () => {
        logout();
        message.success('Erfolgreich abgemeldet');
        navigate('/login');
      },
    });
  };

  const handleExitApp = () => {
    Modal.confirm({
      title: 'App beenden',
      icon: <ExclamationCircleOutlined />,
      content: 'Möchten Sie die App wirklich beenden?',
      okText: 'Ja, beenden',
      okType: 'danger',
      cancelText: 'Abbrechen',
      onOk: async () => {
        if (Capacitor.isNativePlatform()) {
          await App.exitApp();
        } else {
          message.info('App beenden ist nur in der mobilen App verfügbar');
        }
      },
    });
  };

  const platformInfo = {
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/dashboard')}
        style={{ marginBottom: '16px' }}
      >
        Zurück zur Übersicht
      </Button>

      <Title level={2}>
        <ApiOutlined /> Settings
      </Title>

      <Card
        title="API Configuration"
        style={{ marginBottom: '24px' }}
        extra={
          <Tag color={platformInfo.isNative ? 'green' : 'blue'}>
            {platformInfo.platform}
          </Tag>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Current API URL */}
          <div>
            <Text strong>Current API URL:</Text>
            <div style={{
              marginTop: '8px',
              padding: '12px',
              background: '#f5f5f5',
              borderRadius: '4px',
              fontFamily: 'monospace'
            }}>
              {apiUrl}
            </div>
          </div>

          {/* New API URL Input */}
          <div>
            <Text strong>New API URL:</Text>
            <Input
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://api.example.com"
              size="large"
              style={{ marginTop: '8px' }}
              prefix={<ApiOutlined />}
            />
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <Alert
              message={connectionStatus.success ? 'Connection Successful' : 'Connection Failed'}
              description={
                <div>
                  <div>Status: {connectionStatus.status}</div>
                  <div>Message: {connectionStatus.message}</div>
                </div>
              }
              type={connectionStatus.success ? 'success' : 'error'}
              icon={connectionStatus.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              showIcon
            />
          )}

          {/* Action Buttons */}
          <Space wrap>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={newUrl === apiUrl || !newUrl}
            >
              Save
            </Button>

            <Button
              icon={<ApiOutlined />}
              onClick={handleTest}
              loading={testing}
            >
              Test Connection
            </Button>

            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
            >
              Reset to Default
            </Button>

            <Button
              type="dashed"
              onClick={handleReload}
            >
              Reload App
            </Button>
          </Space>
        </Space>
      </Card>

      {/* API Presets Management */}
      <div style={{ marginBottom: '24px' }}>
        <ApiPresets />
      </div>

      {/* Team Management */}
      <TeamManagement />

      {/* Token Management */}
      <TokenManagement />

      {/* Help Card */}
      <Card title="Help & Information">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Paragraph>
            <Text strong>Platform: </Text>
            <Tag color={platformInfo.isNative ? 'green' : 'blue'}>
              {platformInfo.platform}
            </Tag>
            {platformInfo.isNative ? ' (Native App)' : ' (Web Browser)'}
          </Paragraph>

          {/* App Version Info - Only on Native */}
          {appInfo && (
            <Paragraph>
              <Text strong>App Version: </Text>
              <Tag color="purple">v{appInfo.version}</Tag>
              <Text type="secondary"> (Build {appInfo.build})</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ID: {appInfo.id}
              </Text>
            </Paragraph>
          )}

          <Divider />

          <Paragraph>
            <Text strong>For Android Emulator:</Text>
            <br />
            Use <Text code>http://10.0.2.2:86</Text> to connect to localhost on your computer.
          </Paragraph>

          <Paragraph>
            <Text strong>For Real Android/iOS Device:</Text>
            <br />
            Use your computer's local IP address (e.g., <Text code>http://192.168.1.100:86</Text>).
            <br />
            Find your IP: <Text code>ipconfig getifaddr en0</Text> (macOS) or <Text code>ipconfig</Text> (Windows)
          </Paragraph>

          <Paragraph>
            <Text strong>For Production:</Text>
            <br />
            Use <Text code>https://api.billard.flogra.de</Text>
          </Paragraph>

          <Alert
            message="Important"
            description="The API URL is updated immediately after saving. You can test the connection before saving to ensure it works."
            type="info"
            showIcon
          />
        </Space>
      </Card>

      {/* Account & App Management */}
      <Card title="Account & App Verwaltung" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Paragraph>
            Verwalten Sie Ihren Account und die App.
          </Paragraph>

          <Space wrap style={{ width: '100%' }}>
            <Button
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              size="large"
            >
              Abmelden
            </Button>

            {Capacitor.isNativePlatform() && (
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={handleExitApp}
                size="large"
              >
                App beenden
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {/* Debug Logger - Only on Mobile */}
      {Capacitor.isNativePlatform() && (
        <Card title={<><BugOutlined /> Debug Console</>} style={{ marginBottom: '24px' }}>
          <Paragraph>
            View application logs for debugging purposes.
          </Paragraph>
          <DebugLogger />
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
